import { KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { type PolymorphicSegment } from '@repo/poly-hooks/segment';

interface KycBaseParams {
  membershipId?: string;
  userId?: string;
  isMobileBrowser?: boolean;
  provider?: string;
  origin?: string;
  domain?: string;
}

export function kycEvents(prefix: string, base: KycBaseParams, segment: PolymorphicSegment) {
  const sharedProperties = {
    ...(base.membershipId !== undefined && { membership_id: base.membershipId }),
    ...(base.userId !== undefined && { user_id: base.userId }),
    ...(base.isMobileBrowser !== undefined && { is_webview: base.isMobileBrowser }),
    ...(base.origin !== undefined && { origin: base.origin }),
    ...(base.domain !== undefined && { domain: base.domain }),
  };

  return {
    kycSucceeded: (params?: { provider?: string }) => {
      segment.track(`${prefix}_kyc_succeeded`, {
        ...sharedProperties,
        provider: params?.provider ?? base.provider,
        is_new_kyc: true,
      });
    },

    kycSkipped: () => {
      segment.track(`${prefix}_kyc_skipped`, {
        ...sharedProperties,
        provider: 'PROVIDER_UNSPECIFIED',
      });
    },

    kycErrorScreenLoaded: () => {
      segment.track(`${prefix}_kyc_error_screen_loaded`, { ...sharedProperties });
    },

    fourthlineVerificationScreenLoaded: () => {
      segment.track(`${prefix}_fourthline_verification_screen_loaded`, {
        ...sharedProperties,
        provider: base.provider,
      });
    },

    successScreenLoaded: (provider: string) => {
      const isFourthline = provider === KycProvider.fourthline;
      segment.track(
        isFourthline ? `${prefix}_fourthline_success_screen_loaded` : `${prefix}_kyc_succeeded`,
        { ...sharedProperties, provider }
      );
    },

    fourthlineRegenerateLinkClicked: () => {
      segment.track(`${prefix}_fourthline_regenerate_link_clicked`, {
        ...sharedProperties,
        provider: base.provider,
      });
    },

    fourthlineKycFailed: () => {
      segment.track(`${prefix}_kyc_fourthline_failed`, {
        ...sharedProperties,
        provider: base.provider,
      });
    },

    idnowRedirectStarted: () => {
      segment.track(`${prefix}_idnow_redirect_started`, { ...sharedProperties });
    },

    idnowRedirectFailed: () => {
      segment.track(`${prefix}_idnow_redirect_failed`, { ...sharedProperties });
    },

    idnowVerificationScreenLoaded: () => {
      segment.track(`${prefix}_idnow_verification_screen_loaded`, { ...sharedProperties });
    },

    idnowMobileScreenLoaded: () => {
      segment.track(`${prefix}_idnow_mobile_screen_loaded`, { ...sharedProperties });
    },

    idnowSmsSent: () => {
      segment.track(`${prefix}_idnow_sms_sent`, { ...sharedProperties });
    },

    idnowRegenerateLinkClicked: () => {
      segment.track(`${prefix}_idnow_regenerate_link_clicked`, { ...sharedProperties });
    },

    idnowDeviceSwitched: () => {
      segment.track(`${prefix}_idnow_device_switched`, { ...sharedProperties });
    },

    idnowCallStarted: () => {
      segment.track(`${prefix}_idnow_call_started`, { ...sharedProperties });
    },

    idnowDownloadLinkClicked: (link: string) => {
      segment.track(`${prefix}_idnow_download_link_clicked`, { ...sharedProperties, link });
    },

    idnowErrorScreenLoaded: (params?: { reason?: string; status?: string }) => {
      segment.track(`${prefix}_idnow_error_screen_loaded`, {
        ...sharedProperties,
        reason: params?.reason,
        status: params?.status,
      });
    },

    idnowErrorRetrySameDevice: () => {
      segment.track(`${prefix}_idnow_error_retry_started`, { ...sharedProperties });
    },

    idnowErrorSwitchToPhone: () => {
      segment.track(`${prefix}_idnow_error_device_switched`, { ...sharedProperties });
    },

    idnowWaitingResultsScreenLoaded: () => {
      segment.track(`${prefix}_idnow_waiting_results_screen_loaded`, { ...sharedProperties });
    },

    idnowRejectedScreenLoaded: () => {
      segment.track(`${prefix}_idnow_rejected_screen_loaded`, { ...sharedProperties });
    },

    credentialCreationFailed: () => {
      segment.track(`${prefix}_kyc_credential_creation_failed`, {
        ...sharedProperties,
        provider: base.provider,
      });
    },

    verificationBlocked: () => {
      segment.track(`${prefix}_kyc_blocked`, { ...sharedProperties, provider: base.provider });
    },

    // Prefix-free: outside Identity Verification flow

    reKycWarningReviewed: (params: { type: 'required' | 'restricted' | 'suspended' }) => {
      segment.track('re_kyc_memberships_warning_reviewed', {
        ...sharedProperties,
        type: params.type,
      });
    },

    reKycWarningClicked: (params: {
      action: 'close' | 'cta';
      type: 'required' | 'restricted' | 'suspended';
    }) => {
      segment.track('re_kyc_memberships_warning_clicked', {
        ...sharedProperties,
        action: params.action,
        type: params.type,
      });
    },

    expiredPoiWarningClicked: (params: { action: 'cta'; type: 'suspended' }) => {
      segment.track('expired_poi_memberships_warning_clicked', {
        ...sharedProperties,
        action: params.action,
        type: params.type,
      });
    },

    kycCancelled: () => {
      segment.track('submit_kyc_information_cancelled', { ...sharedProperties });
    },

    userInfoSubmitted: () => {
      segment.track('submit_kyc_information_personal_information_confirmed', {
        ...sharedProperties,
      });
    },

    userInfoSubmissionFailed: (params: {
      reason: 'submission' | 'navigation' | 'polling_timeout';
    }) => {
      segment.track('submit_kyc_information_personal_information_failed', {
        ...sharedProperties,
        reason: params.reason,
      });
    },

    kycInformationStart: () => {
      segment.track('submit_kyc_information_start', { ...sharedProperties });
    },

    kycIntroPageViewed: ({ origin }: { origin: string | null }) => {
      segment.track('submit_kyc_information_viewed', { ...sharedProperties, origin });
    },
  };
}
